import { curry } from "ramda";
import { FaEdit, FaEye } from "react-icons/fa";
import {
  brandRequestStatusObject,
  isRequestOpen,
  sortAndFormatOptions,
} from "./functions";

const localStorageKeys = {
  listView: "HBA_MFL_LIST_VIEW",
};

const generateLinkWithParams = curry((link, params) => {
  let newLink = link;
  for (const key of Object.keys(params)) {
    newLink = newLink.replace(key, params[key]);
  }
  return newLink;
});

const linkPlaceholders = {
  brandId: ":brandId",
  brandRequestId: ":brandRequestId",
  sourcingRequestId: ":sourcingRequestId",
  categoryId: ":categoryId",
  crmCustomerId: ":crmCustomerId",
  loanAccountDueDateId: ":loanAccountDueDateId",
  loanAccountId: ":loanAccountId",
  loanAccountTransactionId: ":loanAccountTransactionId",
  loanDepositId: ":loanDepositId",
  loanId: ":loanId",
  loanPaymentId: ":loanPaymentId",
  priceSheetId: ":priceSheetId",
  userId: ":userId",
  vendorBrandId: ":vendorBrandId",
  vendorContactId: ":vendorContactId",
  vendorId: ":vendorId",
  vendorRequestId: ":vendorRequestId",
};

const root = "";
const loansRoot = `${root}/loans_and_accounts`;

const brandListRoot = `${root}/brand_list`;
const brandRequestRoot = `${root}/brand_requests`;
const sourcingRequestRoot = `${root}/sourcing_requests`;
const brandRoot = `${root}/brands`;
const categoryRoot = `${root}/categories`;
const crmRoot = `${root}/crm`;
const emailRoot = `${root}/emails`;
const loanAccountDueDateRoot = `${loansRoot}/loan_accounts_due_dates`;
const loanAccountRoot = `${loansRoot}/loan_accounts`;
const loanDepositRoot = `${loansRoot}/loan_deposits`;
const loanPaymentRoot = `${loansRoot}/loan_payments`;
const loanRoot = `${loansRoot}/loans`;
const priceSheetRoot = `${root}/price_sheets`;
const reportsRoot = `${root}/reports`;
const userRoot = `${root}/users`;
const vendorBrandRoot = `${root}/vendor_brands`;
const vendorContactRoot = `${root}/vendor_contacts`;
const vendorRoot = `${root}/vendors`;
const vendorUserRoot = `${root}/vendor_users`;
const calenderRoot = `${root}/calendar`;

const vendorUsers = {
  root: vendorUserRoot,
  brands: `${vendorUserRoot}/brands`,
  viewBrand: `${vendorUserRoot}/brands/${linkPlaceholders.vendorRequestId}/view`,
  editBrand: `${vendorUserRoot}/brands/${linkPlaceholders.vendorRequestId}/edit`,
  addBrand: `${vendorUserRoot}/brands/add`,
  openBrands: `${vendorUserRoot}/open_brands`,
};
const calenders = {
  root: calenderRoot,
};

const brands = {
  root: brandRoot,
  add: `${brandRoot}/add`,
  edit: `${brandRoot}/${linkPlaceholders.brandId}/edit`,
  changeImage: `${brandRoot}/${linkPlaceholders.brandId}/change_image`,
  changeFile: `${brandRoot}/${linkPlaceholders.brandId}/change_file`,
  attachments: `${brandRoot}/${linkPlaceholders.brandId}/attachments`,
  view: `${brandRoot}/${linkPlaceholders.brandId}/view`,
};

const categories = {
  root: categoryRoot,
  add: `${categoryRoot}/add`,
  edit: `${categoryRoot}/${linkPlaceholders.categoryId}/edit`,
};

const loanAccounts = {
  root: loanAccountRoot,
  add: `${loanAccountRoot}/add`,
  edit: `${loanAccountRoot}/${linkPlaceholders.loanAccountId}/edit`,
  view: `${loanAccountRoot}/${linkPlaceholders.loanAccountId}/view`,
  addTransaction: `${loanAccountRoot}/${linkPlaceholders.loanAccountId}/transactions/add`,
  editTransaction: `${loanAccountRoot}/${linkPlaceholders.loanAccountId}/transactions/${linkPlaceholders.loanAccountTransactionId}/edit`,
};

const loanAccountDueDates = {
  root: loanAccountDueDateRoot,
  add: `${loanAccountDueDateRoot}/add`,
  edit: `${loanAccountDueDateRoot}/${linkPlaceholders.loanAccountDueDateId}/edit`,
};

const loans = {
  root: loanRoot,
  add: `${loanRoot}/add`,
  bulkAdd: `${loanRoot}/bulk_add`,
  edit: `${loanRoot}/${linkPlaceholders.loanId}/edit`,
  view: `${loanRoot}/${linkPlaceholders.loanId}/view`,
  addPayment: `${loanRoot}/${linkPlaceholders.loanId}/payments/add`,
  editPayment: `${loanRoot}/${linkPlaceholders.loanId}/payments/${linkPlaceholders.loanPaymentId}/edit`,
  summary: `${loansRoot}/loan_summary`,
};

const loanPayments = {
  root: loanPaymentRoot,
  add: `${loanPaymentRoot}/add`,
  edit: `${loanPaymentRoot}/${linkPlaceholders.loanPaymentId}/edit`,
};

const loanDeposits = {
  root: loanDepositRoot,
  add: `${loanDepositRoot}/add`,
  edit: `${loanDepositRoot}/${linkPlaceholders.loanDepositId}/edit`,
};

const users = {
  root: userRoot,
  add: `${userRoot}/add`,
  edit: `${userRoot}/${linkPlaceholders.userId}/edit`,
};

const priceSheets = {
  root: priceSheetRoot,
  add: `${priceSheetRoot}/add`,
  edit: `${priceSheetRoot}/${linkPlaceholders.priceSheetId}/edit`,
};

const vendors = {
  root: vendorRoot,
  add: `${vendorRoot}/add`,
  edit: `${vendorRoot}/${linkPlaceholders.vendorId}/edit`,
  view: `${vendorRoot}/${linkPlaceholders.vendorId}/view`,
  addUser: `${vendorRoot}/${linkPlaceholders.vendorId}/add_user`,
};

const vendorContacts = {
  root: vendorContactRoot,
  add: `${vendorContactRoot}/add`,
  edit: `${vendorContactRoot}/${linkPlaceholders.vendorContactId}/edit`,
};

const vendorBrands = {
  root: vendorBrandRoot,
  add: `${vendorBrandRoot}/add`,
  edit: `${vendorBrandRoot}/${linkPlaceholders.vendorBrandId}/edit`,
  view: `${vendorBrandRoot}/${linkPlaceholders.vendorBrandId}/view`,
};

const brandRequests = {
  root: brandRequestRoot,
  brandRequestList: `/brand_request_list`,
  vendorRequests: `/vendor_requests`,
  bulkImport: `/brand_request_bulk_import`,
  deleted: `/deleted_brand_requests`,
  add: `${brandRequestRoot}/add`,
  edit: `${brandRequestRoot}/${linkPlaceholders.brandRequestId}/edit`,
  view: `${brandRequestRoot}/${linkPlaceholders.brandRequestId}/view`,
};

const sourcingRequests = {
  root: sourcingRequestRoot,
  add: `${sourcingRequestRoot}/add`,
  edit: `${sourcingRequestRoot}/${linkPlaceholders.sourcingRequestId}/edit`,
  view: `${sourcingRequestRoot}/${linkPlaceholders.sourcingRequestId}/view`,
};

const crm = {
  root: crmRoot,
  goals: `${crmRoot}/goals`,
  brandMatching: `${crmRoot}/brand_matching`,
  customers: `${crmRoot}/customers`,
  followUps: `${crmRoot}/follow_ups`,
  awaitingReorders: `${crmRoot}/awaiting_reorders`,
  viewCustomer: `${crmRoot}/customers/${linkPlaceholders.crmCustomerId}`,
  brands: `${crmRoot}/brands`,
  viewBrand: `${crmRoot}/brands/${linkPlaceholders.brandId}`,
  viewBrandCustomers: `${crmRoot}/brands/${linkPlaceholders.brandId}/customers`,
  viewBrandOrders: `${crmRoot}/brands/${linkPlaceholders.brandId}/orders`,
  viewBrandOffers: `${crmRoot}/brands/${linkPlaceholders.brandId}/offers`,
  saleOrders: `${crmRoot}/sale_orders`,
  poDashboard: `${crmRoot}/dashboards/po`,
};

const brandList = {
  root: brandListRoot,
  add: `${brandListRoot}/add`,
  edit: `${brandListRoot}/${linkPlaceholders.brandListId}/edit`,
};

const report = {
  root: reportsRoot,
};

const emails = {
  root: emailRoot,
};

const routing = {
  sourcingRequests,
  brandRequests,
  brandList,
  brands,
  calenders,
  categories,
  crm,
  emails,
  home: `${root}/home`,
  loanAccountDueDates,
  loanAccounts,
  loanDeposits,
  loanPayments,
  loans,
  loansRoot,
  priceSheets,
  root,
  users,
  vendorContacts,
  vendorBrands,
  vendors,
  vendorUsers,
  reset: "/reset",
  report,
};

const navLinks = {
  // Edit shortcut disabled per request; brand details can still be edited
  // from inside the brand view page itself.
  brandsForAdmin: [
    { name: <FaEye />, link: generateLinkWithParams(brands.view) },
  ],
  brands: [{ name: <FaEye />, link: generateLinkWithParams(brands.view) }],
  sourcingRequests: [
    { name: <FaEye />, link: generateLinkWithParams(sourcingRequests.view) },
  ],
  categories: [
    { name: <FaEdit />, link: generateLinkWithParams(categories.edit) },
  ],
  crmBrands: [{ name: <FaEye />, link: generateLinkWithParams(crm.viewBrand) }],
  crmCustomers: [
    { name: <FaEye />, link: generateLinkWithParams(crm.viewCustomer) },
  ],
  users: [{ name: <FaEdit />, link: generateLinkWithParams(users.edit) }],
  vendors: [
    { name: <FaEye />, link: generateLinkWithParams(vendors.view) },
    { name: <FaEdit />, link: generateLinkWithParams(vendors.edit) },
  ],
  loans: [
    {
      name: "view",
      link: generateLinkWithParams(loans.view),
      icon: <FaEye />,
    },
    {
      name: "edit",
      link: generateLinkWithParams(loans.edit),
      icon: <FaEdit />,
    },
  ],
  loanPayments: [
    {
      name: <FaEdit />,
      link: generateLinkWithParams(loanPayments.edit),
      icon: <FaEdit />,
    },
  ],
  loanDeposits: [
    {
      name: <FaEdit />,
      link: generateLinkWithParams(loanDeposits.edit),
      icon: <FaEdit />,
    },
  ],
  loanAccounts: [
    {
      name: <FaEye />,
      link: generateLinkWithParams(loanAccounts.view),
    },
    {
      name: <FaEdit />,
      link: generateLinkWithParams(loanAccounts.edit),
      icon: <FaEdit />,
    },
  ],
  loanAccountDueDates: [
    {
      name: <FaEdit />,
      link: generateLinkWithParams(loanAccountDueDates.edit),
      icon: <FaEdit />,
    },
  ],
  loanAccountTransactions: [
    {
      name: <FaEdit />,
      link: generateLinkWithParams(loanAccounts.editTransaction),
      icon: <FaEdit />,
    },
  ],
  vendorContacts: [
    { name: <FaEdit />, link: generateLinkWithParams(vendorContacts.edit) },
  ],
  vendorBrands: [
    { name: <FaEye />, link: generateLinkWithParams(vendorBrands.view) },
    { name: <FaEdit />, link: generateLinkWithParams(vendorBrands.edit) },
  ],
  brandRequests: (isSourcingAdmin) => [
    { name: <FaEye />, link: generateLinkWithParams(brandRequests.view) },
    ...(isSourcingAdmin
      ? [{ name: <FaEdit />, link: generateLinkWithParams(brandRequests.edit) }]
      : []),
  ],
};

const sideNav = ({
  isPriceSheetsUser,
  isPriceSheetsAdmin,
  isSourcingUser,
  isSourcingAdmin,
  isAdmin,
  isSourcingUserOnly,
  isLoansAdmin,
  isLoansUser,
  isBrandListUser,
  isVendorUser,
  brandRequests = [],
  isCrmAdmin,
  isCalender,
}) => [
  {
    name: "Brands",
    sub: [
      {
        name: "Brand List",
        link: routing.brandList.root,
        show: false, // isBrandListUser - hidden from menu per request
      },
      {
        name: "Price Sheets",
        link: routing.brands.root,
        show: false, // isPriceSheetsUser - hidden from menu per request
      },
      {
        name: "Price Sheet Vendors",
        link: routing.vendorBrands.root,
        show: isPriceSheetsAdmin,
      },
      {
        name: "Calendar",
        link: routing.calenders.root,
        show: true,
      },
      {
        name: "Request a New Brand",
        link: routing.sourcingRequests.add,
        show: true,
      },
      {
        name: "View Sourcing Requests",
        link: routing.sourcingRequests.root,
        show: true,
      },
    ],
    show: isBrandListUser || isPriceSheetsUser,
  },
  {
    name: "CRM",
    sub: [
      {
        name: "Brand Matching",
        link: routing.crm.brandMatching,
        show: isCrmAdmin,
      },
      {
        name: "Brands",
        link: routing.crm.brands,
        show: true,
      },
      {
        name: "Customers",
        link: routing.crm.customers,
        show: true,
      },
      {
        name: "Follow Ups",
        link: routing.crm.followUps,
        show: true,
      },
      {
        name: "Awaiting Reorders",
        link: routing.crm.awaitingReorders,
        show: true,
      },
      {
        name: "Goals",
        link: routing.crm.goals,
        show: true,
      },
      {
        name: "PO Dashboard",
        link: routing.crm.poDashboard,
        show: false, //isCrmAdmin,
      },
    ],
    show: true,
  },
  {
    name: "Sourcing",
    sub: [
      {
        name: "Brand List",
        link: routing.brandRequests.brandRequestList,
        show: true,
      },
      {
        name: isSourcingUserOnly ? "Request a Brand" : "Brands",
        link: routing.brandRequests.root,
        show: true,
      },
      {
        name: "Reports",
        link: routing.report.root,
        show: isSourcingAdmin,
      },
      {
        name: "Vendors",
        link: routing.vendors.root,
        show: isSourcingAdmin,
      },
      {
        name: "All Vendor Requests",
        link: routing.brandRequests.vendorRequests,
        show: isSourcingAdmin,
      },
      {
        name: "Vendor Contacts",
        link: routing.vendorContacts.root,
        show: isSourcingAdmin,
      },
      {
        name: "Bulk Import",
        link: routing.brandRequests.bulkImport,
        show: isSourcingAdmin,
      },
      {
        name: "Sent Emails",
        link: routing.emails.root,
        show: isSourcingAdmin,
      },
      {
        name: "Deleted Brands",
        link: routing.brandRequests.deleted,
        show: isSourcingAdmin,
      },
    ],
    show: isSourcingUser,
  },
  {
    name: "Loans",
    sub: [
      {
        name: "Summary",
        link: routing.loans.summary,
        show: isLoansAdmin,
      },
      {
        name: "Loans",
        link: routing.loans.root,
        show: true,
      },
      {
        name: "Payments",
        link: routing.loanPayments.root,
        show: true,
      },
      {
        name: "Deposits",
        link: routing.loanDeposits.root,
        show: true,
      },
    ],
    show: isLoansUser,
  },
  {
    name: "Loan Accounts",
    sub: [
      {
        name: "Accounts",
        link: routing.loanAccounts.root,
        show: true,
      },
      {
        name: "Due Dates",
        link: routing.loanAccountDueDates.root,
        show: true,
      },
    ],
    show: isLoansUser,
  },
  {
    name: "Vendors",
    sub: [
      {
        name: `New Brands (${
          brandRequests.filter((br) => !isRequestOpen(br)).length
        })`,
        link: routing.vendorUsers.brands,
        show: true,
      },
      {
        name: `Open Brands (${brandRequests.filter(isRequestOpen).length})`,
        link: routing.vendorUsers.openBrands,
        show: true,
      },
    ],
    show: isVendorUser,
  },
  {
    name: "Admin",
    sub: [
      {
        name: "Categories",
        link: routing.categories.root,
        show: true,
      },
      {
        name: "Users",
        link: routing.users.root,
        show: true,
      },
    ],
    show: isAdmin,
  },
];

const allBrandRequestStatuses = Object.values(brandRequestStatusObject).map(
  (status) => status.value,
);

const brandClassObject = {
  A: { value: "A", label: "A" },
  B: { value: "B", label: "B" },
  C: { value: "C", label: "C" },
  D: { value: "D", label: "D" },
};

const crmCustomerBrandStatuses = {
  NOT_OFFERED_YET: "Not Offered Yet",
  OFFERED: "Offered",
  ORDERED: "Ordered",
  REJECTED: "Rejected",
  AWAITING_REORDER: "Awaiting Reorder",
  FOLLOW_UP: "Follow Up",
};
const crmCustomerBrandStatusOptions = sortAndFormatOptions({
  list: Object.values(crmCustomerBrandStatuses),
  valueKey: (l) => l,
  labelKey: (l) => l,
});

const allBrandClasses = Object.values(brandClassObject).map(
  (_class) => _class.value,
);

const brandClassDropdownList = Object.values(brandClassObject).map(
  (_class) => _class,
);

export {
  allBrandClasses,
  allBrandRequestStatuses,
  brandClassDropdownList,
  brandClassObject,
  crmCustomerBrandStatuses,
  crmCustomerBrandStatusOptions,
  generateLinkWithParams,
  linkPlaceholders,
  localStorageKeys,
  navLinks,
  routing,
  sideNav,
};
